import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUser } from "@/features/auth/hooks/use-user";
import { useWebAuthnRemoveDevice } from "@/features/auth/hooks/use-webauthn-challenge";
import { WebAuthnRegister } from "@/views/account/sections/components/add-webauthn-key";
import { DotsThreeOutlineIcon, Plus, Shield } from "@phosphor-icons/react";
import { useState } from "react";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import type { DateFormat } from "@/features/utils/dates";

function formatRelativeTime(
  timestamp: number,
  t: (key: string, options?: Record<string, unknown>) => string,
  format: DateFormat["format"],
): string {
  if (timestamp === 0) return t("security.securityKeys.never");
  // Use date-fns to format relative time
  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000); // difference in seconds

  if (diff < 60) return t("security.securityKeys.justNow");
  if (diff < 3600)
    return t("security.securityKeys.minutesAgo", {
      count: Math.floor(diff / 60),
    });
  if (diff < 86400)
    return t("security.securityKeys.hoursAgo", {
      count: Math.floor(diff / 3600),
    });
  return format(date, "PPpp");
}

export const WebAuthnDevicesTable = () => {
  const { t } = useTranslation();
  const { format } = useDateFormat();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { data: user } = useUser();
  const devices = user?.webauthn_devices?.devices || [];

  const removeDeviceMutation = useWebAuthnRemoveDevice();

  const handleRemoveDevice = async (deviceId: string) => {
    removeDeviceMutation.mutate({ deviceId });
  };

  const handleAddSuccess = () => {
    setIsAddDialogOpen(false);
  };

  const isLoading = removeDeviceMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            {t("security.securityKeys.description")}
          </p>
          {devices?.length === 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {t("security.securityKeys.noKeysWarning")}
            </p>
          )}
        </div>
        <Button
          onClick={() => setIsAddDialogOpen(true)}
          size="sm"
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          <Plus className="h-4 w-4" />
          {t("security.securityKeys.addKey")}
        </Button>
      </div>

      <div className="rounded-sm border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.device")}</TableHead>
              <TableHead>{t("security.securityKeys.lastUsed")}</TableHead>
              <TableHead>{t("common.added")}</TableHead>
              <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span>{device.name}</span>
                    <Badge variant="secondary">{t("common.active")}</Badge>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelativeTime(device.last_used_at, t, format)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelativeTime(device.created_at, t, format)}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        disabled={isLoading}
                      >
                        <span className="sr-only">{t("common.openMenu")}</span>
                        <DotsThreeOutlineIcon
                          weight="fill"
                          className="h-4 w-4"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleRemoveDevice(device.id)}
                        className="text-destructive"
                        disabled={isLoading}
                      >
                        {t("common.remove")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {devices?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Shield className="h-8 w-8" />
                    <p>{t("security.securityKeys.noKeys")}</p>
                    <p className="text-sm">
                      {t("security.securityKeys.noKeysDescription")}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <WebAuthnRegister
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};
